<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

import CalulationSegmentDeleteButton from '../components/CalulationSegmentDeleteButton.vue';

import { useCurrentCalculation } from '../composables/useCurrentCalculation';
import { useCurrentCalculatorSegments } from '../composables/useCurrentCalculatorSegments';
import { useEstimatedSegments } from '../composables/useEstimatedSegments';

import type { CalculationSegment } from '../utils/types';

const { currentCalculation } = useCurrentCalculation();
const { getSegmentFirstViewId, getSegmentById, getSegmentFirstGroupId } =
  useCurrentCalculatorSegments();
const { segmentRules } = useEstimatedSegments();

const segmentRulesMap = computed(() => {
  const map = new Map();

  if (!segmentRules.value) {
    return map;
  }

  segmentRules.value.forEach((segment) => {
    map.set(segment.id, segment);
  });

  return map;
});

function getRuleValueBySlug(segmentId: number, slug: string): string {
  const segment = segmentRulesMap.value.get(segmentId);

  if (!segment?.rules) {
    return '-';
  }

  const rule = segment.rules.find((r: any) => r.slug === slug);

  if (!rule) {
    return '-';
  }

  return rule.unit ? `${rule.eval} ${rule.unit}` : String(rule.eval);
}
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center gap-4 font-bold text-gray-900">
      Formularze

      <RouterLink
        class="ml-auto"
        :to="{
          name: 'SelectSegmentView',
          params: {
            clientId: $route.params.client_id,
            calculationId: $route.params.calculationId,
            calculatorId: $route.params.calculatorId,
          },
        }"
      >
        <Button class="font-semibold" size="small" outlined>
          <i class="pi pi-plus" />
          Dodaj technologię
        </Button>
      </RouterLink>
    </div>

    <DataTable
      v-if="currentCalculation"
      class="overflow-hidden rounded-xl border border-gray-200 [&__.p-row-odd]:bg-gray-50"
      :value="currentCalculation?.calculation_segments"
      removableSort
      tableStyle="min-width: 50rem"
    >
      <Column field="id" header="ID" sortable style="width: 5%"></Column>

      <Column header="Nazwa" style="width: 50%">
        <template #body="{ data }">
          {{ getSegmentById(data.segment_id)?.name }}
        </template>
      </Column>

      <Column header="Wartość" style="width: 15%">
        <template #body="{ data }: { data: CalculationSegment }">
          {{ getRuleValueBySlug(data.segment_id, 'cena-zestawu-brutto-dla-klienta') }}
        </template>
      </Column>

      <Column field="dotation" header="Dotacja" style="width: 10%">
        <template #body="{ data }: { data: CalculationSegment }">
          {{ getRuleValueBySlug(data.segment_id, 'dotacja-czyste-powietrze') }}
        </template>
      </Column>

      <Column header="" style="width: 10%">
        <template #body="{ data }: { data: CalculationSegment }">
          <div class="flex gap-4">
            <RouterLink
              :to="{
                name: 'CalculationViewItems',
                params: {
                  ...$route.params,
                  segmentId: data.segment_id,
                  viewId: getSegmentFirstViewId(data.segment_id),
                  groupId: getSegmentFirstGroupId(data.segment_id)?.groupId,
                },
              }"
            >
              <Button class="!p-2" size="small" severity="secondary" outlined>
                <i class="pi pi-pen-to-square"></i>
              </Button>
            </RouterLink>

            <CalulationSegmentDeleteButton
              v-if="!getSegmentById(data.segment_id)?.is_required"
              :calculationUuid="currentCalculation.uuid"
              :segmentId="data.segment_id"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>
